// AI Analytics Service - Database interactions for analytics dashboard
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import type {
  AnalyticsMetrics,
  TopQuestion,
  ModelType,
  AgentType,
  ErrorAnalysis
} from '@/types/ai-admin';

const log = createComponentLogger('AIAnalyticsService', 'ai-analytics');

// Get analytics metrics for a date range
export async function getAnalyticsMetrics(
  dateRange: '7' | '30' | '90' = '30'
): Promise<AnalyticsMetrics> {
  try {
    log.info('Loading analytics metrics', { dateRange });

    // Get user's organization
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
throw new Error('Not authenticated');
}

    const { data: userData } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', user.id)
      .single();

    if (!userData?.organization_id) {
      throw new Error('User has no organization');
    }

    // Calculate date range
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - parseInt(dateRange));

    // Get conversations and messages for the date range
    const { data: conversations } = await supabase
      .from('ai_internal_chat_conversations')
      .select(`
        *,
        ai_internal_chat_messages!inner(*)
      `)
      .eq('organization_id', userData.organization_id)
      .gte('created_at', startDate.toISOString())
      .lte('created_at', endDate.toISOString());

    // Get analytics data
    const { data: analytics } = await supabase
      .from('ai_internal_chat_analytics')
      .select('*')
      .eq('organization_id', userData.organization_id)
      .gte('timestamp', startDate.toISOString())
      .lte('timestamp', endDate.toISOString())
      .order('timestamp', { ascending: true });

    // Get document usage
    const { data: documents } = await supabase
      .from('ai_documents')
      .select('id, title, citation_count, avg_relevance_score')
      .eq('organization_id', userData.organization_id)
      .gt('citation_count', 0)
      .order('citation_count', { ascending: false })
      .limit(10);

    // Process data into metrics
    const metrics = processAnalyticsData(
      conversations || [],
      analytics || [],
      documents || [],
      startDate,
      endDate
    );

    log.info('Analytics metrics loaded successfully');
    return metrics;
  } catch (error) {
    log.error('Failed to load analytics metrics', {}, error as Error);
    throw error;
  }
}

// Process raw data into AnalyticsMetrics format
function processAnalyticsData(
  conversations: any[],
  analytics: any[],
  documents: any[],
  startDate: Date,
  endDate: Date
): AnalyticsMetrics {
  // Calculate summary metrics from ANALYTICS table (the source of truth)
  const totalQueries = analytics.length;
  let totalCost = 0;
  let totalResponseTime = 0;
  let successCount = 0;
  let totalRating = 0;
  let ratedCount = 0;
  const uniqueUsers = new Set<string>();
  const dailyData: Record<string, any> = {};
  const modelUsage: Record<string, any> = {};
  const agentUsage: Record<string, any> = {};
  const errors: any[] = [];

  // Process analytics data (primary source)
  analytics.forEach((record: any) => {
    // Sum costs
    totalCost += record.cost_usd || 0;
    totalResponseTime += record.response_time_ms || 0;

    // Track unique users
    if (record.user_id) {
      uniqueUsers.add(record.user_id);
    }

    // Success = no error
    if (!record.error_occurred) {
      successCount++;
    } else if (record.error_type) {
      errors.push({
        message: record.error_type,
        timestamp: record.timestamp
      });
    }

    // Track ratings
    if (record.user_rating) {
      totalRating += record.user_rating;
      ratedCount++;
    }

    // Track model usage
    const modelKey = record.model_used || 'unknown';
    if (!modelUsage[modelKey]) {
      modelUsage[modelKey] = {
        model: modelKey,
        queries: 0,
        totalCost: 0,
        totalResponseTime: 0,
        totalRating: 0,
        ratedCount: 0
      };
    }
    modelUsage[modelKey].queries++;
    modelUsage[modelKey].totalCost += record.cost_usd || 0;
    modelUsage[modelKey].totalResponseTime += record.response_time_ms || 0;
    if (record.user_rating) {
      modelUsage[modelKey].totalRating += record.user_rating;
      modelUsage[modelKey].ratedCount++;
    }

    // Track agent usage
    const agentKey = record.agent_type || 'unknown';
    if (!agentUsage[agentKey]) {
      agentUsage[agentKey] = 0;
    }
    agentUsage[agentKey] += record.cost_usd || 0;

    // Track daily metrics
    const date = new Date(record.timestamp).toISOString().split('T')[0];
    if (!dailyData[date]) {
      dailyData[date] = {
        date,
        queries: 0,
        cost: 0,
        responseTime: 0,
        responseCount: 0,
        users: new Set()
      };
    }
    dailyData[date].queries++;
    dailyData[date].cost += record.cost_usd || 0;
    dailyData[date].responseTime += record.response_time_ms || 0;
    dailyData[date].responseCount++;
    if (record.user_id) {
      dailyData[date].users.add(record.user_id);
    }
  });

  const successRate = totalQueries > 0 ? (successCount / totalQueries) * 100 : 100;
  const avgResponseTime = totalQueries > 0 ? totalResponseTime / totalQueries : 0;
  const avgRating = ratedCount > 0 ? totalRating / ratedCount : 4.0;

  // Convert daily data to array
  const dailyTrends = Object.values(dailyData).map((day: any) => ({
    date: day.date,
    queries: day.queries,
    cost: day.cost,
    avgResponseTime: day.responseCount > 0 ? day.responseTime / day.responseCount : 0,
    users: day.users.size
  })).sort((a, b) => a.date.localeCompare(b.date));

  // Process model comparison
  const modelComparison = Object.values(modelUsage).map((model: any) => ({
    model: model.model as ModelType,
    queries: model.queries,
    avgCost: model.queries > 0 ? model.totalCost / model.queries : 0,
    avgResponseTime: model.queries > 0 ? model.totalResponseTime / model.queries : 0,
    avgRating: model.ratedCount > 0 ? model.totalRating / model.ratedCount : 0
  }));

  // Process document usage
  const documentUsage = documents.map((doc: any) => ({
    documentId: doc.id,
    title: doc.title,
    citationCount: doc.citation_count || 0,
    percentage: totalQueries > 0 ? ((doc.citation_count || 0) / totalQueries) * 100 : 0,
    avgRelevance: doc.avg_relevance_score || 0.75
  }));

  // Process error analysis
  const errorTypes: Record<string, number> = {};
  errors.forEach((error: any) => {
    const type = error.message.includes('timeout') ? 'Timeout' :
                 error.message.includes('rate') ? 'Rate Limit' :
                 error.message.includes('token') ? 'Token Limit' :
                 'Other';
    errorTypes[type] = (errorTypes[type] || 0) + 1;
  });

  const errorAnalysis: ErrorAnalysis = {
    totalErrors: errors.length,
    errorRate: totalQueries > 0 ? (errors.length / totalQueries) * 100 : 0,
    errorBreakdown: Object.entries(errorTypes).map(([type, count]) => ({
      errorType: type,
      count,
      severity: type === 'Timeout' ? 'high' : 'medium'
    })),
    commonErrors: []
  };

  // Calculate cost projections
  const daysInRange = Math.max(1, Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)));
  const dailyAvg = totalCost / daysInRange;
  const costBreakdown = {
    byModel: Object.fromEntries(
      modelComparison.map(m => [m.model, m.queries * m.avgCost])
    ) as Record<ModelType, number>,
    byAgent: agentUsage as Record<AgentType, number>,
    projection: {
      daily: dailyAvg,
      monthly: dailyAvg * 30,
      annual: dailyAvg * 365
    }
  };

  // Create top questions (placeholder - would need actual question analysis)
  const topQuestions: TopQuestion[] = [];

  return {
    dateRange: {
      start: startDate.toISOString(),
      end: endDate.toISOString()
    },
    summary: {
      totalQueries,
      totalCost,
      avgResponseTime,
      successRate,
      uniqueUsers: uniqueUsers.size,
      avgQueriesPerUser: uniqueUsers.size > 0 ? totalQueries / uniqueUsers.size : 0,
      avgRating
    },
    trends: {
      daily: dailyTrends
    },
    topQuestions,
    documentUsage,
    modelComparison,
    errorAnalysis,
    costBreakdown
  };
}

// Refresh analytics data (triggers recalculation)
export async function refreshAnalytics(): Promise<void> {
  try {
    log.info('Refreshing analytics data');

    // In a real system, this might trigger background jobs
    // For now, we just ensure the data is fresh by clearing any cache

    log.info('Analytics refresh completed');
  } catch (error) {
    log.error('Failed to refresh analytics', {}, error as Error);
    throw error;
  }
}

// Export analytics data to CSV
export function exportAnalyticsToCSV(metrics: AnalyticsMetrics): string {
  const csvData = [];

  // Add summary
  csvData.push(['Summary Metrics']);
  csvData.push(['Total Queries', metrics.summary.totalQueries]);
  csvData.push(['Total Cost', `$${metrics.summary.totalCost.toFixed(2)}`]);
  csvData.push(['Average Response Time', `${metrics.summary.avgResponseTime.toFixed(0)}ms`]);
  csvData.push(['Success Rate', `${metrics.summary.successRate.toFixed(1)}%`]);
  csvData.push(['Unique Users', metrics.summary.uniqueUsers]);
  csvData.push(['Average Rating', metrics.summary.avgRating.toFixed(1)]);
  csvData.push([]);

  // Add daily trends
  csvData.push(['Daily Trends']);
  csvData.push(['Date', 'Queries', 'Cost', 'Avg Response Time', 'Users']);
  metrics.trends.daily.forEach(day => {
    csvData.push([
      day.date,
      day.queries,
      `$${day.cost.toFixed(2)}`,
      `${day.avgResponseTime.toFixed(0)}ms`,
      day.users
    ]);
  });
  csvData.push([]);

  // Add document usage
  csvData.push(['Document Usage']);
  csvData.push(['Document', 'Citations', 'Usage %', 'Avg Relevance']);
  metrics.documentUsage.forEach(doc => {
    csvData.push([
      doc.title,
      doc.citationCount,
      `${doc.percentage.toFixed(1)}%`,
      doc.avgRelevance.toFixed(2)
    ]);
  });

  // Convert to CSV string
  return csvData.map(row => row.join(',')).join('\n');
}